import {connect} from 'react-redux';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkMeetingPropertiesModel,
} from '../../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {
  getFindCalendarVersionName,
  makeGetCalendarData,
} from '../../../../store/data/calendarVersions/calendarVersionsSelectors';
import {getCalendarVersions} from '../../../../store/data/dataSelectors';
import {getCalendarWeekStartDay} from '../../../../store/data/preferences/preferencesSelectors';
import {
  unselectCalendarVersionAction,
  updateViewingNowCalendarsAction,
} from '../../../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetCalendarsPanel,
  makeGetCalendarsParameters,
  makeGetSelectedCalendarsData,
  makeGetSelectedCalendarVersionIds,
  makeGetViewingNowCalendars,
} from '../../../../store/panels/calendars/calendarsPanelSelectors';
import {ViewingNowCalendar} from '../../../../store/panels/calendars/calendarsPanelState';
import {
  addAdditionalGuestsAction,
  selectSchedulingCalendarVersionAction,
} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingActions';
import {
  getSchedulingCalendarVersionId,
  getVisualParticipants,
} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingSelectors';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {SchedulingLinkAvailabilitySettings} from './schedulingLinkAvailabilitySettings';

/*
 * Props.
 */

interface SchedulingLinkAvailabilitySettingsContainerProps {
  panelId: CalendarsPanelId;
  draftLink: DraftSchedulingLinkModel;
  onUpdateMeetingProperties: HandlerOf<Partial<SchedulingLinkMeetingPropertiesModel>>;
  onUpdateSchedulingLink: HandlerOf<Partial<DraftSchedulingLinkModel>>;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SchedulingLinkAvailabilitySettingsContainerProps>()(() => {
  const getCalendarsPanel = makeGetCalendarsPanel();
  const getCalendarsParameters = makeGetCalendarsParameters(getCalendarsPanel);
  const getSelectedCalendarVersionIds = makeGetSelectedCalendarVersionIds(getCalendarsParameters);

  return {
    additionalGuests: getVisualParticipants,
    calendarVersions: getCalendarVersions,
    organizerCalendarData: makeGetCalendarData(getSchedulingCalendarVersionId),
    selectedCalendarsData: makeGetSelectedCalendarsData(getSelectedCalendarVersionIds),
    viewingNowCalendars: makeGetViewingNowCalendars(getCalendarsPanel),
    weekStartDay: getCalendarWeekStartDay,
    findCalendarVersionName: getFindCalendarVersionName,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<SchedulingLinkAvailabilitySettingsContainerProps>()(
  ({panelId}) => ({
    onAddAdditionalGuests: (newGuests: ReadonlyArray<VisualParticipantModel>) =>
      addAdditionalGuestsAction(
        panelId,
        newGuests.map((guest) => guest.contact.handle),
      ),
    onUpdateCalendarVersionId: (newId) => selectSchedulingCalendarVersionAction(panelId, newId),
    onUpdateViewingNowCalendars: (viewingNowCalendars) =>
      updateViewingNowCalendarsAction(panelId, viewingNowCalendars),
    onUnselectCalendarVersion: (calendarVersionId) =>
      unselectCalendarVersionAction(panelId, calendarVersionId, false),
  }),
);

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: SchedulingLinkAvailabilitySettingsContainerProps,
) {
  const {viewingNowCalendars} = stateProps;
  const {onUpdateViewingNowCalendars} = dispatchProps;
  const {draftLink, onUpdateMeetingProperties, onUpdateSchedulingLink} = ownProps;

  const onRemoveViewingNowCalendar = (viewingNowToRemove: ViewingNowCalendar) => {
    const newViewingNowCalendars = viewingNowCalendars.filter(
      (viewingNow) => viewingNow.id !== viewingNowToRemove.id,
    );
    onUpdateViewingNowCalendars(newViewingNowCalendars);
  };

  const onUpdateAdditionalGuests = (guests: ReadonlyArray<VisualParticipantModel>) =>
    onUpdateMeetingProperties({additionalGuests: guests.map((guest) => guest.contact.handle)});
  const onUpdateAdvanceNoticeMinutes = (advanceNoticeMinutes: number) =>
    onUpdateSchedulingLink({advanceNoticeMinutes});
  const onUpdateStartTimeIncrements = (startTimeIncrements: number) =>
    onUpdateSchedulingLink({startTimeIncrements});
  const onUpdateMeetingBufferMinutes = (meetingBufferMinutes: number) =>
    onUpdateSchedulingLink({meetingBufferMinutes});

  const {advanceNoticeMinutes, startTimeIncrements, meetingBufferMinutes} = draftLink;

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    advanceNoticeMinutes,
    startTimeIncrements,
    meetingBufferMinutes,
    onRemoveViewingNowCalendar,
    onUpdateAdditionalGuests,
    onUpdateAdvanceNoticeMinutes,
    onUpdateStartTimeIncrements,
    onUpdateMeetingBufferMinutes,
  };
}

export const SchedulingLinkAvailabilitySettingsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(SchedulingLinkAvailabilitySettings);
